/* The HUB-C02 profile shapes, hand-written against the pinned contract in
   docs/stories/central-hub/m2-rulings.md; `npm run gen:api` proves them. */

export type Level = "header" | "line" | "tax";
export type Requirement = "required" | "conditional" | "optional";

export const MODES = ["field", "constant", "computed", "blank", "unmapped"] as const;
export type Mode = (typeof MODES)[number];

export interface Issue {
  severity: string;
  message: string;
}

export interface RowDto {
  key: string;
  ota: string;
  title: string;
  requirement: string;
  mode: string | null;
  source: string | null;
  value: string | null;
  transform: string | null;
  args: Record<string, unknown> | null;
  derived: boolean;
  preview: string;
  issues: Issue[];
}

export interface GroupDto {
  key: string;
  title: string;
  cardinality: string;
  requirement: string;
  rows: RowDto[];
  groups: GroupDto[];
}

export interface MappingCoverage {
  resolved: number;
  total: number;
}

export interface Blocker {
  key: string;
  message: string;
}

export interface ProfileRef {
  id: string;
  version: number;
  state: string;
  entityId: string;
}

export interface StateBody {
  docType: string;
  /** The document family this profile serves (HUB-F05): sales, selfbilled or rcm. */
  family: string;
  groups: GroupDto[];
  coverage: MappingCoverage;
  exportReady: boolean;
  blockers: Blocker[];
  warnings: Blocker[];
  profile: ProfileRef;
  readonly: boolean;
}

export interface ProfileVersionRef {
  id: string;
  version: number;
  state: string;
  publishedAt: string | null;
  publishedBy: string | null;
}

export interface ProfileSummary {
  id: string;
  entityId: string;
  docType: string;
  /** One profile per family (HUB-F05); the list carries one item per family that has one. */
  family: string;
  activeVersion: number | null;
  latestVersion: number;
  versions: ProfileVersionRef[];
  /** The active version's stored coverage; null while nothing is published. */
  coverage: MappingCoverage | null;
}

/** The body of a row write; the server answers with the whole StateBody. */
export interface MappingEntryDto {
  mode: string;
  source?: string | null;
  value?: string | null;
  transform?: string | null;
  args?: Record<string, unknown> | null;
}

/** GET /api/transforms — the catalogue behind the computed-mode picker. */
export interface TransformArgDto {
  name: string;
  type: string;
  required: boolean;
}

export interface TransformDto {
  name: string;
  title: string;
  args: TransformArgDto[];
  description: string;
}

/** POST .../preview — one rendered string per ota, plus the recomputed score. */
export interface PreviewBody {
  previews: Record<string, string>;
  coverage: MappingCoverage;
  blockers: Blocker[];
  exportReady: boolean;
}

/** POST .../validate */
export interface ValidationBody {
  exportReady: boolean;
  blockers: Blocker[];
  warnings: Blocker[];
}

/** GET /api/standard-fields — the catalogue metadata RowDto deliberately omits. */
export interface StandardFieldDto {
  key: string;
  ota: string;
  ibt: string | null;
  title: string;
  group: string;
  level: Level;
  requirement: string;
  cardinality: string;
  datatype: string;
  codeList: string[] | null;
  ublPath: string | null;
  derived: boolean;
}

export interface ErpFieldDto {
  name: string;
  label: string;
  fieldtype: string;
  level: Level;
  options?: string[] | null;
}

/** What a row needs beyond RowDto: the caption's ibt, its UBL path, its code list, its note. */
export interface RowMeta {
  label?: string | undefined;
  ibt?: string | null | undefined;
  level?: Level | undefined;
  ublPath?: string | null | undefined;
  codeList?: string[] | null | undefined;
  note?: string | undefined;
}

/** The rail's own view: the server's score plus the gap jump-list. */
export interface CoverageDto extends MappingCoverage {
  gaps: string[];
  gapLabels: Record<string, { label?: string | undefined; ibt?: string | null | undefined }>;
}

export type Expand = { open: boolean; seq: number };

// Coverage counts the same way the server does (studio.engine): a row is applicable
// when it is always required, covered when it is gateway-derived or has a real entry.
export function isApplicable(row: RowDto): boolean {
  return row.requirement === "required";
}

export function isCovered(row: RowDto): boolean {
  if (row.derived) return true;
  return row.mode !== null && row.mode !== "blank" && row.mode !== "unmapped";
}

export type Score = { covered: number; applicable: number };

/** A group's badge counts its own rows and every descendant's. */
export function scoreOf(group: GroupDto): Score {
  const score: Score = { covered: 0, applicable: 0 };
  for (const row of group.rows) {
    if (!isApplicable(row)) continue;
    score.applicable += 1;
    if (isCovered(row)) score.covered += 1;
  }
  for (const child of group.groups) {
    const inner = scoreOf(child);
    score.covered += inner.covered;
    score.applicable += inner.applicable;
  }
  return score;
}

export function gapsOf(groups: GroupDto[]): RowDto[] {
  return groups.flatMap((group) => [
    ...group.rows.filter((row) => isApplicable(row) && !isCovered(row)),
    ...gapsOf(group.groups),
  ]);
}

/** Shows unsaved edits in the tree, so Discard is simply an empty draft. */
export function applyDraft(
  groups: GroupDto[],
  dirtyRows: Record<string, MappingEntryDto | null>,
): GroupDto[] {
  if (Object.keys(dirtyRows).length === 0) return groups;
  return groups.map((group) => ({
    ...group,
    rows: group.rows.map((row) => {
      const entry = dirtyRows[row.ota];
      return entry ? { ...row, ...entry } : row;
    }),
    groups: applyDraft(group.groups, dirtyRows),
  }));
}

export function rowStatus(row: RowDto): string {
  if (row.derived) return "derived";
  if (row.issues.length > 0) return "error";
  return isCovered(row) ? "mapped" : "unmapped";
}
